import { apiV2, signInWithCustomToken } from "api/firebase";
import {
  cancelSessionSwitch,
  markSessionSwitchInProgress,
} from "tools/auth/sessionSync";
import { logoutUser } from "tools/auth/logoutCleanup";
import {
  LastLoginMethod,
  writeLastLoginMethod,
} from "tools/auth/lastLoginMethod";
import { impersonationStore } from "tools/auth/impersonationStore";
import { APP_PATH, LOGIN_PATH } from "view/routes";

type LoginUserOptions = {
  email: string;
  password: string;
  redirectTo?: string;
};

const hasActiveSession = async (): Promise<boolean> => {
  const me = await apiV2.user.getMeSafe().catch(() => null);
  return Boolean(me);
};

const buildLoginRedirectPath = (redirectTo: string): string => {
  if (redirectTo === APP_PATH) {
    return LOGIN_PATH;
  }
  return `${LOGIN_PATH}?redirectPathAfterLogin=${encodeURIComponent(
    redirectTo
  )}`;
};

export const loginUser = async ({
  email,
  password,
  redirectTo = APP_PATH,
}: LoginUserOptions): Promise<void> => {
  const isActiveSession = await hasActiveSession();

  if (isActiveSession) {
    await logoutUser({
      reason: "manual",
      redirect: false,
      broadcast: false,
    });

    const { impersonationToken } = await apiV2.auth.impersonateByCredentials({
      email,
      password,
    });

    impersonationStore.set(impersonationToken);
    window.location.replace(buildLoginRedirectPath(redirectTo));
    return;
  }

  const result: unknown = await apiV2.auth.logIn({
    email,
    password,
  });

  if (
    typeof result === "object" &&
    result !== null &&
    "twoFactorRequired" in result
  ) {
    throw new Error(
      "Two-factor authentication is not supported on this login flow."
    );
  }

  const { customToken } = result as { customToken: string };

  markSessionSwitchInProgress();
  try {
    await signInWithCustomToken(customToken);
  } catch (error) {
    // `signInWithCustomToken` already wrote `markSessionSwitchInProgress`'s
    // localStorage flag and emitted `switch-start`. If Firebase rejected
    // the custom token (rare — backend only just minted it), bubble the
    // error AND release sibling /login tabs from their loader.
    cancelSessionSwitch();
    throw error;
  }

  // The session has started, so this is the sign-in the login page's hint
  // should describe. The impersonation branch above returns before this point
  // — an impersonated session must not overwrite the real user's method.
  writeLastLoginMethod(LastLoginMethod.Password);

  window.location.replace(redirectTo);
};
